import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import GuestDetailsModal from './GuestDetailsModal';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatStatus(pass) {
  if (pass.status === 'used') return 'Arrived';
  if (pass.status === 'expired') return 'Expired';
  if (pass.status === 'revoked') return 'Revoked';
  if (pass.isPassGenerated === false) return 'Registered';
  return 'Pending';
}

function formatStatusClass(pass) {
  if (pass.status === 'used') return 'badge-success';
  if (pass.status === 'expired') return 'badge-danger';
  if (pass.status === 'revoked') return 'badge-secondary';
  if (pass.isPassGenerated === false) return 'badge-info';
  return 'badge-warning';
}

export default function EventDetailsModal({ eventId, onClose }) {
  const { faculty: reqFaculty } = useAuth();
  const [event, setEvent] = useState(null);
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);

  useEffect(() => {
    if (!eventId) return undefined;

    let active = true;
    setLoading(true);

    Promise.all([
      api.get(`/events/${eventId}`),
      api.get('/passes', { params: { eventId, withNoPass: 'true' } }),
    ])
      .then(([eventRes, passRes]) => {
        if (!active) return;
        setEvent(eventRes.data.event || null);
        setPasses(passRes.data.passes || []);
      })
      .catch((err) => {
        console.error('Error loading event details:', err);
        if (active) {
          setEvent(null);
          setPasses([]);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [eventId]);

  const handleApprove = async () => {
    try {
      setLoading(true);
      await api.post(`/events/${eventId}/approve`);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve');
    } finally {
      setLoading(false);
    }
  };

  if (!eventId) return null;

  const guestCount = passes.filter((pass) => pass.status !== 'revoked').length;
  const coordinator = event?.faculty || {};

  return (
    <>
      <div 
        className="modal-overlay" 
        role="presentation" 
        style={{ display: selectedPass ? 'none' : 'flex' }}
      >
      <div className="modal event-modal" style={{ maxWidth: '44rem' }}>
        <div className="modal-header">
          <div className="modal-title">Event details</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="page-loading" style={{ minHeight: '18rem' }}>
              <div className="spinner" />
            </div>
          ) : event ? (
            <>
              <div className="pass-hero-block">
                <div className="pass-hero-label">Event name</div>
                <div className="pass-hero-code" style={{ fontSize: '1.4rem', wordBreak: 'break-word' }}>
                  {event.title}
                </div>
                <div className="event-detail-subtitle">{event.type || 'other'}</div>
              </div>

              <div className="event-detail-grid">
                <div className="pass-detail-item"><span className="pass-detail-label">Venue</span><span className="pass-detail-value">{event.venue || '—'}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Mode</span><span className="pass-detail-value" style={{ textTransform: 'capitalize' }}>{event.eventMode || 'Offline'}</span></div>
                <div className="pass-detail-item">
                  <span className="pass-detail-label">Date</span>
                  <span className="pass-detail-value" style={{ fontWeight: 700, color: 'var(--navy)' }}>
                    {event.isMultiDay && event.endDate ? (
                      <>
                        {new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        {' - '}
                        {new Date(event.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </>
                    ) : formatDate(event.date)}
                  </span>
                </div>
                <div className="pass-detail-item"><span className="pass-detail-label">Guests</span><span className="pass-detail-value">{guestCount}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Host</span><span className="pass-detail-value">{coordinator.name || '—'}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Host email</span><span className="pass-detail-value">{coordinator.email || '—'}</span></div>
              </div>

              {/* Collaborators list */}
              {event.collaborators?.length > 0 && (
                <div style={{ marginTop: '0.375rem' }}>
                  <div style={{ fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--navy)', marginBottom: '0.75rem' }}>
                    Collaborators ({event.collaborators.length})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem' }}>
                    {event.collaborators.map(c => (
                      <div key={c._id} style={{ padding: '0.875rem 1rem', background: 'rgba(0,95,167,0.05)', borderRadius: '14px', border: '1px solid rgba(0,95,167,0.12)' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-1)' }}>{c.name}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: '0.125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.department || 'University Faculty'}</div>
                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', fontSize: '0.72rem' }}>
                          <span style={{ color: 'var(--navy)', fontWeight: 600 }}>{c.email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approval Status Card Removed - All events are auto-approved */}

              <div className="event-guest-list" style={{ marginTop: '0.375rem' }}>
                <div className="card-head" style={{ marginBottom: '0.75rem' }}>
                  <h3 className="card-heading" style={{ fontSize: '1rem' }}>Guest list ({passes.length})</h3>
                </div>

                {passes.length === 0 ? (
                  <div className="empty-state empty-state-soft">
                    <div className="empty-icon">Nothing here</div>
                    <div className="empty-text">No guests issued yet</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                    {passes.map((pass, index) => (
                      <div 
                        key={pass._id} 
                        className="guest-preview-card"
                        onClick={() => setSelectedPass(pass)}
                      >
                        <div className="guest-preview-avatar">
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div className="guest-preview-content">
                          <div className="guest-preview-name">{pass.guestName || '—'}</div>
                          <div className="guest-preview-meta">
                            <div style={{ fontWeight: 600, color: 'var(--text-2)' }}>{pass.profession || ''}</div>
                            <div>{pass.organization || ''}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="empty-state empty-state-soft">
              <div className="empty-icon">Nothing here</div>
              <div className="empty-text">Event details not found</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      </div>
      {selectedPass && (
        <GuestDetailsModal pass={selectedPass} onClose={() => setSelectedPass(null)} />
      )}
    </>
  );
}
